// ---------------------------------------------------------------------------
// 案内とメンテナンスの停止期間（docs/95、2026-10-27）
//   管理アプリの「案内」で作る運営からの知らせ。区分は4つ（お知らせ・注意・メンテナンス・緊急）。出る場所は3つ：
//   ①ログイン後のホームの帯（Support.tsx の AnnouncementBanner。閉じたら出ない）
//   ②ログイン前の画面の帯（components/NoticeBand.tsx。「ログイン画面」オンの案内。閉じられない）＝GET /api/v1/notices
//   ③停止期間の止めの画面 e（メンテナンス中です）＝API の 503 MAINTENANCE（stop.ts の STOP_MSG.maintenance）
//   文はここ1か所（ホームの帯・ログイン前の帯・管理アプリのできあがりの見本が同じ関数で組み立てる）
// ---------------------------------------------------------------------------

/** 区分（製品 DB の announcements.kind） */
export type NoticeKind = "info" | "warn" | "maintenance" | "emergency";
export const NOTICE_KINDS: readonly NoticeKind[] = ["info", "warn", "maintenance", "emergency"];

/** 印の色：藍（お知らせ・メンテナンス）／朱（注意・緊急）。色だけで伝えない＝印の文字（区分の名前）を必ず添える */
export const noticeTone = (k: NoticeKind): "ai" | "shu" => (k === "warn" || k === "emergency" ? "shu" : "ai");

/** ログイン前の帯の1件（GET /api/v1/notices）。人・会社の情報は持たない */
export type PublicNotice = {
  id: string;
  kind: NoticeKind;
  title: string;
  body: string;
  /** 停止期間（メンテナンスだけ。無ければ null） */
  blockFrom: string | null;
  blockUntil: string | null;
};
/** GET /api/v1/notices の答え。stop＝これからか今の停止期間（無ければ null） */
export type NoticesResponse = { at: string; items: PublicNotice[]; stop: { from: string; until: string } | null };

/** 日本時間の年・月・日・曜日・時・分 */
function jst(iso: string): { y: number; m: number; d: number; w: string; h: number; mi: number } | null {
  const t = new Date(iso);
  if (Number.isNaN(t.getTime())) return null;
  const j = new Date(t.getTime() + 9 * 3_600_000);
  return { y: j.getUTCFullYear(), m: j.getUTCMonth() + 1, d: j.getUTCDate(), w: "日月火水木金土"[j.getUTCDay()]!, h: j.getUTCHours(), mi: j.getUTCMinutes() };
}
const hm = (h: number, mi: number) => `${h}:${String(mi).padStart(2, "0")}`;

/**
 * 停止期間の言い方（日本時間）：「10月30日（金）18:00〜24:00」。
 * 終わりが翌日の 0:00 ちょうどなら「24:00」、日をまたぐときは「10月30日（金）18:00〜31日（土）0:15」、月もまたぐときは「〜11月1日（日）0:15」
 */
export function noticePeriodText(fromIso: string, untilIso: string): string {
  const a = jst(fromIso);
  const b = jst(untilIso);
  if (!a || !b) return "";
  const head = `${a.m}月${a.d}日（${a.w}）${hm(a.h, a.mi)}〜`;
  const sameDay = a.y === b.y && a.m === b.m && a.d === b.d;
  if (sameDay) return head + hm(b.h, b.mi);
  // 翌日の 0:00 ちょうど＝その日の 24:00
  const nextDay = new Date(Date.UTC(a.y, a.m - 1, a.d + 1));
  if (b.h === 0 && b.mi === 0 && b.y === nextDay.getUTCFullYear() && b.m === nextDay.getUTCMonth() + 1 && b.d === nextDay.getUTCDate()) return head + "24:00";
  const month = a.y === b.y && a.m === b.m ? "" : `${b.m}月`;
  return `${head}${month}${b.d}日（${b.w}）${hm(b.h, b.mi)}`;
}

/** 本文の1行目（止めの画面 e の補足・docs/95 ★10）。空なら null */
export function noticeFirstLine(body: string | null | undefined): string | null {
  const line = (body ?? "").split(/\r?\n/).map((x) => x.trim()).find((x) => x !== "");
  return line ?? null;
}

export const NOTICE_MSG = {
  /** 区分の名前（印の文字。管理アプリの区分の選択肢も同じ） */
  kind: { info: "お知らせ", warn: "注意", maintenance: "メンテナンス", emergency: "緊急" } as Record<NoticeKind, string>,
  /** 帯の読み上げ名 */
  region: (k: NoticeKind) => `運営からの${NOTICE_MSG.kind[k]}`,
  /** ログイン前の帯のメンテナンスの1行（停止期間があるとき） */
  blockLine: (fromIso: string, untilIso: string) => `${noticePeriodText(fromIso, untilIso)} はご利用いただけません`,
  /** ログイン前の帯の「詳細」（本文を開く・もう一度押すと閉じる。帯そのものは閉じられないので「閉じる」の字は使わない） */
  detail: "詳細",
  /** ログイン前の帯の並べ方：緊急 → メンテナンス → 注意 → お知らせ（ホームの帯は 緊急 → 注意 → メンテナンス → お知らせ＝DB） */
  loginOrder: ["emergency", "maintenance", "warn", "info"] as NoticeKind[],
} as const;

/** ログイン前の帯の1行（区分の印のあとに続く文）。メンテナンスで停止期間があれば期間、ほかは題 */
export function noticeLoginLine(n: Pick<PublicNotice, "kind" | "title" | "blockFrom" | "blockUntil">): string {
  if (n.kind === "maintenance" && n.blockFrom && n.blockUntil) return NOTICE_MSG.blockLine(n.blockFrom, n.blockUntil);
  return n.title;
}

/** ログイン前の帯の「詳細」で開く文。メンテナンスの期間の行のときは題＋本文、ほかは本文（空なら「詳細」を出さない） */
export function noticeLoginDetail(n: Pick<PublicNotice, "kind" | "title" | "body" | "blockFrom" | "blockUntil">): string {
  const body = n.body.trim();
  if (n.kind === "maintenance" && n.blockFrom && n.blockUntil) return body ? `${n.title}\n${body}` : n.title;
  return body;
}
